/**
 * interviewPrepUnlock.js — RC 1.50 polish-clarification
 *
 * Tracks the "Workspace Interview Prep deliverable unlocked" state
 * for a (user, job, source CV) tuple in localStorage.
 *
 * Why?
 * The backend `analyze_fit` baseline run (issued from the Job
 * Detail page) writes an `interview_prep` payload onto
 * `job_scores.reasoning_json.interview_prep` as a SIDE EFFECT —
 * the baseline diagnostic produces interview intelligence
 * internally even though the operator's product rule says the
 * Workspace **Interview Prep** is a SEPARATE PAID DELIVERABLE
 * unlocked only when the user clicks "Generate Interview Prep" in
 * the Workspace.
 *
 * Without a separate unlock signal, the Workspace would auto-show
 * "Interview prep ready" on mount whenever a baseline analyse-fit
 * had ever been run — masking the click-to-unlock contract.
 *
 * This frontend-only marker fixes that:
 *   - On a successful Workspace "Generate Interview Prep" click →
 *     writes the marker.
 *   - Hydration / cache-hit paths CHECK the marker before showing
 *     the 5-bucket render. If absent, the section shows the
 *     "not yet generated for this run" calm state with the Run
 *     CTA.
 *
 * Hard rails:
 *   - Frontend only. No backend / schema / pricing / Stripe / Credits
 *     contract changes. The existing analyze_fit cache-hit refund
 *     behaviour is preserved verbatim.
 *   - localStorage (not sessionStorage) so once the user has
 *     unlocked the deliverable for a (user, job, CV) they don't
 *     have to re-click after a tab close.
 *   - Per-tuple key — never bleed across users / jobs / CVs.
 *   - Pure functions. Defensive on missing globals (SSR-safe).
 */

const VERSION = 1;
const ROOT_KEY = 'ndor.workspace.interview_prep.unlock.v1';

function _readMap() {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(ROOT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch {
    return {};
  }
}

function _writeMap(map) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(ROOT_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode — fail open */
  }
}

function _key({ userId, jobId, sourceType, sourceId }) {
  if (!userId || !jobId) return null;
  // sourceType is 'uploaded_cv' | 'cv_variant' (backend-canonical).
  // sourceId is the source CV / variant id. If either missing,
  // we still produce a deterministic key for the (user, job)
  // tuple so the marker survives even if the source binding is
  // missing on read (defensive).
  const t = (sourceType || 'unknown');
  const s = (sourceId || 'unknown');
  return `${userId}::${jobId}::${t}::${s}`;
}

/**
 * Returns true when the Workspace Interview Prep deliverable has
 * been unlocked for (user, job, source).
 */
export function isInterviewPrepUnlocked(opts) {
  const k = _key(opts || {});
  if (!k) return false;
  const map = _readMap();
  const v = map[k];
  return !!(v && v.v === VERSION);
}

/**
 * Marks the Workspace Interview Prep deliverable unlocked for
 * (user, job, source). Idempotent.
 *
 * RC 1.51 — accepts an optional `payload` arg containing the
 * generated interview_prep result so the workspace can rehydrate
 * the 5-bucket render after section-switch / refresh WITHOUT
 * touching the baseline `/best-fit` surface (operator rule:
 * Interview Prep is blind to baseline analyse_fit hydration).
 */
export function markInterviewPrepUnlocked(opts) {
  const k = _key(opts || {});
  if (!k) return;
  const map = _readMap();
  const prev = map[k] || {};
  map[k] = {
    v: VERSION,
    t: Date.now(),
    // Preserve any payload already on the row when called without one
    // (e.g. a legacy click that didn't pass payload yet).
    payload: (opts && Object.prototype.hasOwnProperty.call(opts, 'payload'))
      ? opts.payload
      : (prev.payload ?? null),
  };
  _writeMap(map);
}

/**
 * RC 1.51 — return the persisted Workspace Interview Prep payload
 * for (user, job, source), or null when no payload exists.
 *
 * Shape (operator-locked, frontend-only marker):
 *   {
 *     interview_prep: <5-bucket payload>,
 *     score:          { id, analyse_run_id } | null,
 *     billing:        { ... } | null,
 *     cachedAt:       <epoch ms>,
 *   }
 *
 * Returns null when the marker is absent OR the marker exists but
 * carries no payload (legacy unlocks pre-RC 1.51).
 */
export function getInterviewPrepResult(opts) {
  const k = _key(opts || {});
  if (!k) return null;
  const map = _readMap();
  const v = map[k];
  if (!v || v.v !== VERSION) return null;
  return v.payload ?? null;
}

/**
 * Test / cleanup hook — clear all unlock markers.
 */
export function clearAllInterviewPrepUnlocks() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(ROOT_KEY);
  } catch {
    /* no-op */
  }
}

export default {
  isInterviewPrepUnlocked,
  markInterviewPrepUnlocked,
  getInterviewPrepResult,
  clearAllInterviewPrepUnlocks,
};
